/* ─────────────────────────────────────────────────────────────────────────────
   PTL → PTL Billing — RTK Query slice.

   Backend: proship_mongo /api/ptl/billing/* (ROLE_PROSHIPADMIN + PTL_ACCESS; settings,
   terms, diesel price, approve and files also need PTL_BILLING). Payloads come back
   directly. Errors keep the server's status + message so pages show it verbatim:
   400 = a rule failed, 409 = someone else changed it, 403 = no access.

   Contract (JSON unless noted):
     GET  config                         → settings (cut-off, close day, caps, slabs …)
     PUT  config {fields}                → settings
     GET  terms?merchantOid=             → [terms version]
     POST terms {…}                      → terms version
     DELETE terms/:id                    → { message }
     GET  cycles/:month                  → { cycleMonth, status, dieselPrice, updatedBy, updatedAt }
     PUT  cycles/:month/diesel-price {dieselPrice}
     GET  cycles/:month/summary          → stored lines (same as Review): { shipments, clients, blocked, flagCounts, flagMessages, totals…, movedOut }
     GET  cycles/:month/preview?merchantOid=  → { shipments, blocked, flagCounts, totals…, lines[] }
     POST cycles/:month/prepare?merchantOid=  → { created, refreshed, unchanged, frozen, removed, blocked }
     GET  adhoc/types                    → [{ code, label }]
     GET  adhoc/awb-search?q=            → [{ awb, orderOid, merchantName, toCity, lineStatus, locked … }]
     GET  adhoc?merchantOid&state&awb&cycleMonth&page&size → { cards…, total, rows[] }
     GET  adhoc/order/:orderOid          → [charge]
     POST adhoc {…} · PUT adhoc/:id {…} · DELETE adhoc/:id?version=
     POST adhoc/proof (multipart file)   → { proofKey, proofFileName }
     GET  adhoc/:id/proof                → { url }
     GET  adhoc/bulk/template            → xlsx (blob)
     POST adhoc/bulk (multipart file, proofs, confirm) → { okRows, skippedRows, savedRows, rows[] }
     POST lines/search {filter}          → { rows[], total, totals, statusCards{}, lossCount, lowestCourier, lowestLaneWeight, couriers[], lanes[] }
     POST lines/clients {filter}         → [client row]
     POST lines/selectable?limit= {filter} → [{ id, version, merchantOid, totalExclGst }]
     GET  lines/:id                      → { line, adhoc[], history[], podLinks[], shipment{} }
     POST lines/approve { lines:[{id,version}] }      (PTL_BILLING)
     POST lines/:id/reopen { version, note }          (PTL_BILLING)
     POST lines/:id/resubmit { version, note }
     POST cycles/:month/rollover                       (PTL_BILLING)
     POST files { cycleMonth, scope, merchantOid }     (PTL_BILLING) → file record
     GET  files?month=                                 → [file record]
     GET  files/:id/download?internal=                 → xlsx / zip (blob; internal needs PTL_BILLING)
     GET  overview/:month/in-transit?limit=            → { count, redCount, buckets[], rows[] }
     GET  overview/:month/in-transit.csv               → csv (blob)
     GET  overview/:month/loss-clients                 → [{ merchantName, marginPct, worstLane, worstCourier, atMinFreight, freightRisePct … }]
     GET  terms/check?month=                           → [{ merchantOid, merchantName, ptlFlag, shipments, termsInForce, missing }]
     GET  terms/bulk/template?month=                   → xlsx (blob)
     POST terms/bulk (multipart file, confirm)         (PTL_BILLING)
     GET  agreements/:merchantOid                      → { signed, startDate, expiryDate, validity, daysToExpiry, remarks, version }
     PUT  agreements/:merchantOid { startDate, expiryDate, remarks, version }   (PTL_BILLING)
     DELETE agreements/:merchantOid                    (PTL_BILLING)
   ──────────────────────────────────────────────────────────────────────────── */
import { createApi } from '@reduxjs/toolkit/query/react'
import { apiConstants, deleteGeneric, get, post, postMultipart, put } from '../common/constants'

export const BILLING_BASE = apiConstants.PTL_BILLING

const run = async (promise) => {
  try {
    const res = await promise
    return { data: res?.data }
  } catch (err) {
    const r = err?.response
    return { error: { status: r?.status, data: r?.data, message: r?.data?.message || err?.message || 'Request failed' } }
  }
}

const clean = (o) => Object.fromEntries(Object.entries(o || {}).filter(([, v]) => v !== undefined && v !== null && v !== ''))

export const ptlBillingApi = createApi({
  reducerPath: 'ptlBillingApi',
  baseQuery: () => ({ data: undefined }), // every endpoint has its own queryFn
  tagTypes: ['Config', 'Terms', 'Cycle', 'Preview', 'Adhoc', 'Lines', 'Files', 'Overview', 'Agreement', 'SurchargeTypes'],
  keepUnusedDataFor: 60,
  refetchOnMountOrArgChange: 30,
  endpoints: (b) => ({
    billingConfig: b.query({
      queryFn: () => run(get(`${BILLING_BASE}/config`)),
      providesTags: ['Config'],
    }),
    updateBillingConfig: b.mutation({
      queryFn: (body) => run(put(`${BILLING_BASE}/config`, body)),
      invalidatesTags: ['Config', 'Preview'],
    }),

    billingTerms: b.query({
      queryFn: (merchantOid) => run(get(`${BILLING_BASE}/terms`, { merchantOid })),
      providesTags: (_r, _e, m) => [{ type: 'Terms', id: m }],
    }),
    addBillingTerms: b.mutation({
      queryFn: (body) => run(post(`${BILLING_BASE}/terms`, body)),
      invalidatesTags: (_r, _e, body) => [{ type: 'Terms', id: body?.merchantOid }, 'Terms', 'Preview', 'Agreement'],
    }),
    removeBillingTerms: b.mutation({
      queryFn: ({ id }) => run(deleteGeneric(`${BILLING_BASE}/terms/${id}`)),
      invalidatesTags: (_r, _e, a) => [{ type: 'Terms', id: a?.merchantOid }, 'Preview'],
    }),

    billingCycle: b.query({
      queryFn: (month) => run(get(`${BILLING_BASE}/cycles/${month}`)),
      providesTags: (_r, _e, m) => [{ type: 'Cycle', id: m }],
    }),
    setDieselPrice: b.mutation({
      queryFn: ({ month, dieselPrice }) => run(put(`${BILLING_BASE}/cycles/${month}/diesel-price`, { dieselPrice })),
      invalidatesTags: (_r, _e, a) => [{ type: 'Cycle', id: a?.month }, 'Preview'],
    }),
    // the month as stored (what Billing Review shows); refreshed by prepare, ad hoc, approval and the nightly job
    cycleSummary: b.query({
      queryFn: (month) => run(get(`${BILLING_BASE}/cycles/${month}/summary`)),
      providesTags: ['Preview', 'Lines'],
    }),
    billingPreview: b.query({
      queryFn: ({ month, merchantOid }) => run(get(`${BILLING_BASE}/cycles/${month}/preview`, clean({ merchantOid }))),
      providesTags: ['Preview'],
    }),
    prepareCycle: b.mutation({
      queryFn: ({ month, merchantOid }) => {
        const q = merchantOid ? `?merchantOid=${encodeURIComponent(merchantOid)}` : ''
        return run(post(`${BILLING_BASE}/cycles/${month}/prepare${q}`))
      },
      invalidatesTags: ['Preview', 'Adhoc', 'Lines'],
    }),

    surchargeTypes: b.query({
      queryFn: () => run(get(`${BILLING_BASE}/surcharge/types`)),
      providesTags: ['SurchargeTypes'],
    }),
    // manage surcharge types (Billing Terms): list with stop dates, add, rename, stop / restart
    surchargeTypeList: b.query({
      queryFn: () => run(get(`${BILLING_BASE}/surcharge/types/manage`)),
      providesTags: ['SurchargeTypes'],
    }),
    addSurchargeType: b.mutation({
      queryFn: (label) => run(post(`${BILLING_BASE}/surcharge/types`, { label })),
      invalidatesTags: ['SurchargeTypes'],
    }),
    renameSurchargeType: b.mutation({
      queryFn: ({ code, label }) => run(put(`${BILLING_BASE}/surcharge/types/${code}`, { label })),
      invalidatesTags: ['SurchargeTypes'],
    }),
    stopSurchargeType: b.mutation({
      queryFn: ({ code, from }) => run(put(`${BILLING_BASE}/surcharge/types/${code}/stop`, { from: from || null })),
      invalidatesTags: ['SurchargeTypes', 'Config', 'Preview'],
    }),
    adhocTypes: b.query({
      queryFn: () => run(get(`${BILLING_BASE}/adhoc/types`)),
      keepUnusedDataFor: 5 * 60,
    }),
    adhocAwbSearch: b.query({
      queryFn: (q) => run(get(`${BILLING_BASE}/adhoc/awb-search`, { q })),
      keepUnusedDataFor: 30,
    }),
    adhocList: b.query({
      queryFn: (filter) => run(get(`${BILLING_BASE}/adhoc`, clean(filter))),
      providesTags: ['Adhoc'],
    }),
    adhocForOrder: b.query({
      queryFn: (orderOid) => run(get(`${BILLING_BASE}/adhoc/order/${orderOid}`)),
      providesTags: ['Adhoc'],
    }),
    createAdhoc: b.mutation({
      queryFn: (body) => run(post(`${BILLING_BASE}/adhoc`, body)),
      invalidatesTags: ['Adhoc', 'Preview', 'Lines'],
    }),
    updateAdhoc: b.mutation({
      queryFn: ({ id, ...body }) => run(put(`${BILLING_BASE}/adhoc/${id}`, body)),
      invalidatesTags: ['Adhoc', 'Preview', 'Lines'],
    }),
    deleteAdhoc: b.mutation({
      queryFn: ({ id, version }) => run(deleteGeneric(`${BILLING_BASE}/adhoc/${id}?version=${version ?? ''}`)),
      invalidatesTags: ['Adhoc', 'Preview', 'Lines'],
    }),
    uploadAdhocProof: b.mutation({
      queryFn: (file) => {
        const fd = new FormData()
        fd.append('file', file)
        return run(postMultipart(`${BILLING_BASE}/adhoc/proof`, fd))
      },
    }),
    adhocBulk: b.mutation({
      queryFn: ({ file, proofs, confirm }) => {
        const fd = new FormData()
        fd.append('file', file)
        if (proofs) fd.append('proofs', proofs)
        fd.append('confirm', confirm ? 'true' : 'false')
        return run(postMultipart(`${BILLING_BASE}/adhoc/bulk`, fd))
      },
      invalidatesTags: (_r, _e, a) => (a?.confirm ? ['Adhoc', 'Preview', 'Lines'] : []),
    }),

    searchLines: b.query({
      queryFn: (filter) => run(post(`${BILLING_BASE}/lines/search`, filter)),
      providesTags: ['Lines'],
    }),
    clientRows: b.query({
      queryFn: (filter) => run(post(`${BILLING_BASE}/lines/clients`, filter)),
      providesTags: ['Lines'],
    }),
    selectableLines: b.mutation({
      queryFn: ({ filter, limit }) => run(post(`${BILLING_BASE}/lines/selectable?limit=${limit || 50}`, filter)),
    }),
    lineDetail: b.query({
      queryFn: (id) => run(get(`${BILLING_BASE}/lines/${id}`)),
      providesTags: (_r, _e, id) => [{ type: 'Lines', id }],
      keepUnusedDataFor: 0,
    }),
    approveLines: b.mutation({
      queryFn: (lines) => run(post(`${BILLING_BASE}/lines/approve`, { lines })),
      invalidatesTags: ['Lines', 'Adhoc'],
    }),
    reopenLine: b.mutation({
      queryFn: ({ id, version, note }) => run(post(`${BILLING_BASE}/lines/${id}/reopen`, { version, note })),
      invalidatesTags: ['Lines', 'Adhoc'],
    }),
    resubmitLine: b.mutation({
      queryFn: ({ id, version, note }) => run(post(`${BILLING_BASE}/lines/${id}/resubmit`, { version, note })),
      invalidatesTags: ['Lines', 'Adhoc'],
    }),
    billingFiles: b.query({
      queryFn: (month) => run(get(`${BILLING_BASE}/files`, clean({ month }))),
      providesTags: ['Files'],
    }),
    generateFile: b.mutation({
      queryFn: (body) => run(post(`${BILLING_BASE}/files`, body)),
      invalidatesTags: ['Files', 'Lines', 'Adhoc'],
    }),
    inTransit: b.query({
      queryFn: ({ month, limit }) => run(get(`${BILLING_BASE}/overview/${month}/in-transit`, { limit: limit ?? 5 })),
      providesTags: ['Overview'],
    }),
    lossClients: b.query({
      queryFn: (month) => run(get(`${BILLING_BASE}/overview/${month}/loss-clients`)),
      providesTags: ['Overview', 'Lines'],
    }),
    termsCheck: b.query({
      queryFn: (month) => run(get(`${BILLING_BASE}/terms/check`, { month })),
      providesTags: ['Terms'],
    }),
    termsBulk: b.mutation({
      queryFn: ({ file, confirm }) => {
        const fd = new FormData()
        fd.append('file', file)
        fd.append('confirm', confirm ? 'true' : 'false')
        return run(postMultipart(`${BILLING_BASE}/terms/bulk`, fd))
      },
      invalidatesTags: (_r, _e, a) => (a?.confirm ? ['Terms', 'Preview'] : []),
    }),
    agreement: b.query({
      queryFn: (merchantOid) => run(get(`${BILLING_BASE}/agreements/${merchantOid}`)),
      providesTags: (_r, _e, m) => [{ type: 'Agreement', id: m }],
    }),
    saveAgreement: b.mutation({
      queryFn: ({ merchantOid, ...body }) => run(put(`${BILLING_BASE}/agreements/${merchantOid}`, body)),
      invalidatesTags: (_r, _e, a) => [{ type: 'Agreement', id: a?.merchantOid }, 'Lines'],
    }),
    removeAgreement: b.mutation({
      queryFn: (merchantOid) => run(deleteGeneric(`${BILLING_BASE}/agreements/${merchantOid}`)),
      invalidatesTags: (_r, _e, m) => [{ type: 'Agreement', id: m }, 'Lines'],
    }),
    rolloverCycle: b.mutation({
      queryFn: (month) => run(post(`${BILLING_BASE}/cycles/${month}/rollover`)),
      invalidatesTags: ['Lines', 'Cycle', 'Preview'],
    }),
  }),
})

export const {
  useBillingConfigQuery,
  useUpdateBillingConfigMutation,
  useBillingTermsQuery,
  useAddBillingTermsMutation,
  useRemoveBillingTermsMutation,
  useBillingCycleQuery,
  useSetDieselPriceMutation,
  useBillingPreviewQuery,
  useCycleSummaryQuery,
  usePrepareCycleMutation,
  useAdhocTypesQuery,
  useSurchargeTypesQuery,
  useSurchargeTypeListQuery,
  useAddSurchargeTypeMutation,
  useRenameSurchargeTypeMutation,
  useStopSurchargeTypeMutation,
  useAdhocAwbSearchQuery,
  useAdhocListQuery,
  useAdhocForOrderQuery,
  useCreateAdhocMutation,
  useUpdateAdhocMutation,
  useDeleteAdhocMutation,
  useUploadAdhocProofMutation,
  useAdhocBulkMutation,
  useSearchLinesQuery,
  useClientRowsQuery,
  useSelectableLinesMutation,
  useLineDetailQuery,
  useApproveLinesMutation,
  useReopenLineMutation,
  useResubmitLineMutation,
  useRolloverCycleMutation,
  useBillingFilesQuery,
  useGenerateFileMutation,
  useInTransitQuery,
  useLossClientsQuery,
  useTermsCheckQuery,
  useTermsBulkMutation,
  useAgreementQuery,
  useSaveAgreementMutation,
  useRemoveAgreementMutation,
} = ptlBillingApi
